import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKeywordStatsRecompute } from '../../shared/api/hooks/monitor/keyword/useKeywordStatsRecompute';
import { invalidateBrandStats } from '../../shared/api/query/invalidateBrandStats';
import './KeywordsPage.scss';
import Title from '../../shared/components/title/Title';
import { useTranslation } from 'react-i18next';
import Loader from '../../shared/components/common/loader/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import { DataTable } from '../../shared/components/common/data-table/DataTable';
import { SOV_PIE_CHART_PALETTE } from '../../shared/utils/sovPalette';
import { SELECTED_BRAND_COLOR } from '../../shared/utils/utils';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Paginator } from 'primereact/paginator';
import SimpleBar from 'simplebar-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { PROJECTS } from '../../shared/routes/path';
import { useGetProjectKeywords } from '../../shared/api/hooks/monitor/keyword/useGetProjectKeywords';
import useToast from '../../shared/hooks/global/UseToast';
import { useGetKeywordDomains } from '../../shared/api/hooks/monitor/keyword/useGetKeywordDomains';
import KeywordDomainsCell, { OwnedKeywordDomain } from './components/keyword-domains/KeywordDomainsCell';
import {
  domainsByKeywordOf,
  groupDomainsOf as groupDomainsOfShared,
  primaryDomainOf as primaryDomainOfShared,
} from '../../shared/helpers/keyword/BrandDomainHelper';
import BrandDialog, { BrandDialogTab } from './components/brand-dialog/BrandDialog';
import KeywordAliasesCell from './components/keyword-aliases/KeywordAliasesCell';
import BrandLabelCell from './components/brand-label/BrandLabelCell';
import type { Keyword, KeywordGroup, KeywordStatus } from '@brand24-original/llm-types';
import {
  bulkUpdateKeywordStatus,
  updateKeywordStatusByCanonical,
} from '../../shared/services/monitor/keywords/KeywordsService';
import CustomTooltip from '../../shared/components/common/tooltip/CustomTooltip';
import EmptyState from '../../shared/components/common/empty-state/EmptyState';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import MobileHeaderSearch from '../../shared/components/common/mobile-menu/MobileHeaderSearch';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../shared/utils/mobile';

type FilterMode = 'ALL' | 'ACTIVE' | 'INACTIVE';


const KeywordsPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { accountResponse, projectResponse } = useAccount();

  useEffect(() => {
    if (accountResponse && !accountResponse.addons.includes('KEYWORDS_PAGE')) {
      navigate(PROJECTS, { replace: true });
    }
  }, [accountResponse, navigate]);

  const { keywords, isLoading } = useGetProjectKeywords(projectIdNumber);
  const { keywordDomains } = useGetKeywordDomains(projectIdNumber);
  const { pendingBrands } = useKeywordStatsRecompute(projectIdNumber);

  const [filterMode, setFilterMode] = useState<FilterMode>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const { showToast, toastElement } = useToast();
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState<KeywordGroup[]>([]);
  // Sortowanie i stronicowanie trzymamy sami: paginator stoi POD SimpleBarem, więc tabela dostaje
  // tylko bieżącą stronę — gdyby sortowała ją sama, sortowałaby jedną stronę, a nie całą listę.
  // Domyślnie po wzmiankach, a przy remisie po nazwie (patrz `sortedKeywords`).
  const [sortField, setSortField] = useState<'keyword' | 'promptCount' | null>('promptCount');
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(-1);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  // Otwarte okno marki. Trzymamy sam kanonik, a markę bierzemy z bieżącej listy — po każdej zmianie
  // (dołączenie, odłączenie, domena) okno pokazuje świeże dane, a marka, która zniknęła, je zamyka.
  const [editing, setEditing] = useState<{ canonical: string; tab: BrandDialogTab; color: string } | null>(
    null
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const lastEditingGroup = useRef<KeywordGroup | null>(null);
  const domainsByKeyword = useMemo(() => domainsByKeywordOf(keywordDomains), [keywordDomains]);

  const groupDomainsOf = useCallback(
    (group: KeywordGroup): OwnedKeywordDomain[] => groupDomainsOfShared(group, domainsByKeyword),
    [domainsByKeyword]
  );

  // Ikona marki: favicon jej pierwszej pokazywanej domeny; bez domeny zostaje kolorowa literka.
  const primaryDomainOf = useCallback(
    (group: KeywordGroup) => primaryDomainOfShared(group, domainsByKeyword),
    [domainsByKeyword]
  );

  const handleMutationError = useCallback(() => {
    showToast(t('keywordUpdateError'), 'error');
  }, [showToast, t]);


  const invalidateKeywords = useCallback(
    () => {
      void invalidateBrandStats(queryClient);
    },
    [queryClient]
  );

  // Helper: every keyword id in a group (canonical row + all aliases).
  const allIdsInGroup = (kw: KeywordGroup): number[] => [
    kw.id,
    ...kw.aliases.map((a) => a.id),
  ];

  const bulkActivateMutation = useMutation({
    mutationFn: () => {
      const ids = selectedKeywords.flatMap(allIdsInGroup);
      return bulkUpdateKeywordStatus(ids, 'ACTIVE');
    },
    onSuccess: () => {
      setSelectedKeywords([]);
      invalidateKeywords();
    },
    onError: handleMutationError,
  });

  const bulkDeactivateMutation = useMutation({
    mutationFn: () => {
      const ids = selectedKeywords.flatMap(allIdsInGroup);
      return bulkUpdateKeywordStatus(ids, 'INACTIVE');
    },
    onSuccess: () => {
      setSelectedKeywords([]);
      invalidateKeywords();
    },
    onError: handleMutationError,
  });

  // Flip the whole canonical group with one server round-trip.
  const groupToggleMutation = useMutation({
    mutationFn: async ({ canonical, status }: { canonical: string; status: KeywordStatus }) =>
      updateKeywordStatusByCanonical({ canonical, projectId: projectIdNumber, status }),
    // Zwrócona obietnica trzyma `isPending` aż do odświeżenia listy — spinner w wierszu nie znika,
    // zanim przełącznik pokaże nowy stan.
    onSuccess: () => invalidateBrandStats(queryClient),
    onError: handleMutationError,
  });

  const currentProject = useMemo(() => {
    if (!projectResponse || !projectIdNumber) return null;
    return projectResponse.find((p) => p.id === projectIdNumber) ?? null;
  }, [projectResponse, projectIdNumber]);

  const defaultKeyword = currentProject?.defaultKeyword ?? '';
  const firmName = currentProject?.firmName ?? '';

  const filteredKeywords = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return keywords
      .filter((kw) => {
        // Status MARKI (którakolwiek nazwa aktywna), nie samej nazwy głównej.
        if (filterMode === 'ACTIVE') return kw.brandStatus === 'ACTIVE';
        if (filterMode === 'INACTIVE') return kw.brandStatus === 'INACTIVE';
        return true;
      })
      .filter((kw) => {
        if (!query) return true;
        // Match against the canonical name OR any alias display string.
        if (kw.keyword.toLowerCase().includes(query)) return true;
        if (kw.canonical.toLowerCase().includes(query)) return true;
        return kw.aliases.some((a) => a.keyword.toLowerCase().includes(query));
      });
  }, [keywords, filterMode, searchQuery]);

  // Marka projektu stoi zawsze na górze, niezależnie od sortowania — to punkt odniesienia dla reszty.
  const isProjectBrand = useCallback(
    (kw: KeywordGroup) => kw.canonical === defaultKeyword || kw.normalizedKeyword === defaultKeyword,
    [defaultKeyword]
  );

  const sortedKeywords = useMemo(() => {
    // Po nazwie, którą widać w kolumnie — etykieta marki, a bez niej jej nazwa.
    const byName = (a: KeywordGroup, b: KeywordGroup) =>
      (a.label ?? a.keyword).localeCompare(b.label ?? b.keyword, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    // Wzmianki mają mnóstwo remisów; bez drugiego klucza ich kolejność zależała od odpowiedzi serwera
    // i wiersze skakały po każdym odświeżeniu (np. przełączeniu statusu). Nazwa rozstrzyga remis.
    const sorted =
      !sortField || sortOrder === 0
        ? [...filteredKeywords].sort(byName)
        : [...filteredKeywords].sort((a, b) =>
            sortField === 'promptCount'
              ? ((a.promptCount ?? 0) - (b.promptCount ?? 0)) * sortOrder || byName(a, b)
              : byName(a, b) * sortOrder
          );
    return [...sorted.filter(isProjectBrand), ...sorted.filter((kw) => !isProjectBrand(kw))];
  }, [filteredKeywords, sortField, sortOrder, isProjectBrand]);

  // Wyłączenie marki przy filtrze „aktywne” skraca listę — ostatnia strona mogłaby zostać pusta.
  const pageFirst =
    first < sortedKeywords.length ? first : Math.max(0, Math.floor((sortedKeywords.length - 1) / rows) * rows);
  const pageItems = sortedKeywords.slice(pageFirst, pageFirst + rows);

  // Marka projektu to GRUPA, w której siedzi `defaultKeyword` albo wybrana `firmName` — także jako wariant.
  const ownRole = (kw: KeywordGroup): 'default' | 'firm' | null => {
    const names = [kw.canonical, ...[kw, ...kw.aliases].map((member) => member.normalizedKeyword)];
    if (defaultKeyword && names.includes(defaultKeyword)) return 'default';
    if (firmName && names.includes(firmName.toLowerCase())) return 'firm';
    return null;
  };

  // Marki projektu nie wyłącza się w całości (musi mieć choć jedną aktywną nazwę) — włączyć zawsze wolno.
  const isGroupToggleDisabled = (kw: KeywordGroup): boolean =>
    kw.brandStatus === 'ACTIVE' && ownRole(kw) !== null;

  const getDisabledReason = (kw: KeywordGroup): string => {
    if (!isGroupToggleDisabled(kw)) return '';
    return t(ownRole(kw) === 'default' ? 'keywordToggleDisabledDefault' : 'keywordToggleDisabledFirm');
  };

  const handleGroupToggle = (kw: KeywordGroup) => {
    if (isGroupToggleDisabled(kw)) return;
    const newStatus: KeywordStatus = kw.brandStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    groupToggleMutation.mutate({ canonical: kw.canonical, status: newStatus });
  };

  const hasAnyInactive = selectedKeywords.some((kw) => {
    const current = filteredKeywords.find((f) => f.canonical === kw.canonical);
    return (current ?? kw).brandStatus === 'INACTIVE';
  });

  const hasAnyActive = selectedKeywords.some((kw) => {
    const current = filteredKeywords.find((f) => f.canonical === kw.canonical);
    return (current ?? kw).brandStatus === 'ACTIVE';
  });

  const isRowSelectable = useCallback(
    (kw: KeywordGroup): boolean => {
      if (kw.canonical === defaultKeyword || kw.normalizedKeyword === defaultKeyword) return false;
      if (kw.canonical === firmName || kw.normalizedKeyword === firmName) return false;
      return true;
    },
    [defaultKeyword, firmName]
  );

  // Kolor marki z tej samej palety, co wykresy raportu — marka projektu dostaje kolor „wybranej”,
  // reszta kolejne barwy palety wg pozycji na liście. Nie trzymamy koloru w bazie; jest wyliczany
  // deterministycznie, żeby ta sama marka miała ten sam kolor przy każdym renderze.
  const colorOfBrand = useCallback(
    (row: KeywordGroup, index: number): string => {
      const isOwn =
        row.canonical === defaultKeyword ||
        row.normalizedKeyword === defaultKeyword ||
        row.canonical === firmName ||
        row.normalizedKeyword === firmName;
      return isOwn
        ? SELECTED_BRAND_COLOR
        : SOV_PIE_CHART_PALETTE[index % SOV_PIE_CHART_PALETTE.length];
    },
    [defaultKeyword, firmName]
  );

  if (!accountResponse || !projectResponse || !projectId) {
    navigate(PROJECTS);
    return <></>;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (filteredKeywords.length === 0 && !searchQuery && filterMode === 'ALL') {
    return (
      <div className="keywords-page page-content">
        <div className="page-box">
          <Title
            title={t('brands')}
            childrenOnMain={<div className="title-box__subtitle">{t('brandsPageDesc')}</div>}
          />
          <div className="empty-state-page">
            <EmptyState emptyStateData={{ title: t('keywordsEmptyState') }} />
          </div>
        </div>
      </div>
    );
  }

  // Marka wybrana do analizy na dashboardzie (`firmName`, zapisany może w innej wielkości liter) —
  // jej wiersz to ten, w którym siedzi ta nazwa, także jako wariant.
  const isSelectedBrand = (group: KeywordGroup) =>
    [group, ...group.aliases].some((member) => member.normalizedKeyword === firmName.toLowerCase());

  // ── Cell renderers ──────────────────────────────────────────────────────────

  // Nazwę (etykietę) zmienia się w oknie marki, w zakładce „Podstawowe”.
  const brandBodyTemplate = (row: KeywordGroup, options: { rowIndex: number }) => (
    <BrandLabelCell
      group={row}
      domain={primaryDomainOf(row)}
      color={colorOfBrand(row, options.rowIndex)}
      isMain={isProjectBrand(row)}
      isSelected={isSelectedBrand(row)}
      onOpen={() => openBrand(row, 'basic', options.rowIndex)}
    />
  );

  // Marki, które można dołączyć POD daną markę: widoczne na liście, bez niej samej i bez marki
  // projektu — tej nie wolno przenieść pod inną (statystyki szukają jej po jej własnej nazwie; backend
  // też to odrzuca).
  const containsProjectBrand = (group: KeywordGroup) =>
    [group, ...group.aliases].some(
      (member) => member.normalizedKeyword === defaultKeyword || member.normalizedKeyword === firmName
    );

  const targetsFor = (group: KeywordGroup) =>
    filteredKeywords
      .filter((brand) => brand.canonical !== group.canonical && !containsProjectBrand(brand))
      .map((brand) => ({ normalizedKeyword: brand.normalizedKeyword, name: brand.label ?? brand.keyword }));

  // Strona jest tylko dla kont z addonem, a tam tagi nie grają. Nazwę marki projektu wolno wyłączyć, dopóki
  // marka ma inną aktywną nazwę — zablokowana jest tylko OSTATNIA aktywna (backend pilnuje tego samego).
  const isLockedName = (alias: Keyword) => {
    const group = editingGroup;
    if (!group || ownRole(group) === null || alias.status !== 'ACTIVE') return false;
    return ![group, ...group.aliases].some(
      (member) => member.status === 'ACTIVE' && member.normalizedKeyword !== alias.normalizedKeyword
    );
  };

  const openBrand = (row: KeywordGroup, tab: BrandDialogTab, rowIndex: number) => {
    setEditing({ canonical: row.canonical, tab, color: colorOfBrand(row, rowIndex) });
    setIsDialogOpen(true);
  };

  const trackedNamesBodyTemplate = (row: KeywordGroup, options: { rowIndex: number }) => (
    <KeywordAliasesCell
      group={row}
      domain={primaryDomainOf(row)}
      color={colorOfBrand(row, options.rowIndex)}
      onOpen={() => openBrand(row, 'names', options.rowIndex)}
    />
  );

  const domainsBodyTemplate = (row: KeywordGroup, options: { rowIndex: number }) => (
    <KeywordDomainsCell
      domains={groupDomainsOf(row)}
      onOpen={() => openBrand(row, 'domains', options.rowIndex)}
    />
  );

  // Okno zamyka się z animacją — do jej końca musi pokazywać ostatnią markę, inaczej najpierw traci
  // treść (kurczy się), a dopiero potem znika. Dotyczy to i zamknięcia, i marki, która zniknęła z listy
  // (dołączona do innej) — wtedy okno się zamyka, ale na starej treści.
  const liveEditingGroup = editing ? keywords.find((kw) => kw.canonical === editing.canonical) ?? null : null;
  if (liveEditingGroup) {
    lastEditingGroup.current = liveEditingGroup;
  }
  const editingGroup = liveEditingGroup ?? lastEditingGroup.current;

  const statusBodyTemplate = (row: KeywordGroup) => {
    // Włączona marka, której statystyki worker jeszcze liczy — spinner zamiast przełącznika.
    if ([row, ...row.aliases].some((member) => pendingBrands.includes(member.normalizedKeyword))) {
      return (
        <div className="keywords-page__status" title={t('statsRecomputingBrand')}>
          <span className="keywords-page__recomputing" aria-label={t('statsRecomputingBrand')} />
        </div>
      );
    }
    // Kliknięta marka: spinner od kliknięcia do odświeżenia listy, żeby było widać, że coś się dzieje.
    if (groupToggleMutation.isPending && groupToggleMutation.variables?.canonical === row.canonical) {
      return (
        <div className="keywords-page__status" aria-busy="true">
          <span className="keywords-page__recomputing" />
        </div>
      );
    }
    const disabled = isGroupToggleDisabled(row) || groupToggleMutation.isPending;
    return (
      <div
        className="keywords-page__status"
        title={isGroupToggleDisabled(row) ? getDisabledReason(row) : undefined}
        onClick={() => {
          if (disabled) return;
          handleGroupToggle(row);
        }}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
      >
        <InputSwitch
          className="input-switch--sm"
          checked={row.brandStatus === 'ACTIVE'}
          disabled={disabled}
        />
      </div>
    );
  };

  const promptCountBodyTemplate = (row: KeywordGroup) => (
    <span className="keyword-prompt-count">{row.promptCount ?? 0}</span>
  );

  const filterOptions = [
    { label: t('keywordFilterAll'), value: 'ALL' },
    { label: t('keywordFilterActive'), value: 'ACTIVE' },
    { label: t('keywordFilterInactive'), value: 'INACTIVE' },
  ];

  return (
    <div className="keywords-page page-content">
      <div className="page-box">
        {/* Telefon: tytuł, wyszukiwarka i tryb zaznaczania w górnym pasku; filtr statusu zostaje pod nim. */}
        <MobilePageHeader
          title={<span>{t('brands')}</span>}
          actions={
            <>
              <MobileHeaderSearch
                value={searchQuery}
                onChange={(value) => {
                  setSearchQuery(value);
                  setFirst(0);
                }}
              />
              <button
                type="button"
                className={`mobile-header__icon-btn${selectionMode ? ' mobile-header__icon-btn--active' : ''}`}
                onClick={() => {
                  setSelectionMode((prev) => {
                    if (prev) setSelectedKeywords([]);
                    return !prev;
                  });
                }}
                aria-label={t('keywordSelectMode')}
              >
                <span className="ico--select ico--sm" />
              </button>
            </>
          }
        />
        {/* Przyklejony nagłówek jak na Źródłach — globalny `page-box__filters` + `useStickyObserver`. */}
        {!isMobile && (
          <div className="page-box__filters" data-observe-sticky>
            <Title
              title={t('brands')}
              childrenOnMain={<div className="title-box__subtitle">{t('brandsPageDesc')}</div>}
            />
          </div>
        )}

        <div className="page-box__stats">
          <div className="table-content">
            <div className="keywords-page__filter-bar">
              <Dropdown
                value={filterMode}
                options={filterOptions}
                onChange={(e) => {
                  setFilterMode(e.value);
                  setFirst(0);
                }}
                className="dropdown dropdown--btn-sm keywords-page__status-dropdown"
              />
              <div className="page-box__buttons">
                {selectionMode && selectedKeywords.length > 0 && (
                  <div className="page-box__buttons-panel">
                    {hasAnyInactive && (
                      <>
                        <Button
                          className="action-btn"
                          onClick={() => bulkActivateMutation.mutate()}
                          disabled={bulkActivateMutation.isPending}
                          id="bulk-activate-keywords-btn"
                        >
                          <span className="ico--run ico--xs" />
                        </Button>
                        <CustomTooltip
                          tooltipText="keywordBulkActivate"
                          targetRef="bulk-activate-keywords-btn"
                        />
                      </>
                    )}
                    {hasAnyActive && (
                      <>
                        <Button
                          className="action-btn"
                          onClick={() => bulkDeactivateMutation.mutate()}
                          disabled={bulkDeactivateMutation.isPending}
                          id="bulk-deactivate-keywords-btn"
                        >
                          <span className="ico--pause ico--xs" />
                        </Button>
                        <CustomTooltip
                          tooltipText="keywordBulkDeactivate"
                          targetRef="bulk-deactivate-keywords-btn"
                        />
                      </>
                    )}
                  </div>
                )}
                {!isMobile && (
                  <>
                    <div className="input-search-box">
                      <InputText
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setFirst(0);
                        }}
                        placeholder={t('searchKeywords')}
                        className="input-search"
                      />
                    </div>
                    <Button
                      className={`action-btn${selectionMode ? ' action-btn--active' : ''}`}
                      onClick={() => {
                        setSelectionMode((prev) => {
                          if (prev) setSelectedKeywords([]);
                          return !prev;
                        });
                      }}
                      id="keyword-select-mode-btn"
                    >
                      <span className="ico--select ico--xs" />
                    </Button>
                    <CustomTooltip tooltipText="keywordSelectMode" targetRef="keyword-select-mode-btn" />
                  </>
                )}
              </div>
            </div>

            {/* Układ jak w zespole i na liście projektów: SimpleBar jest właścicielem przewijania, a
                `scrollable` to warunek działania zamrożonej kolumny statusu. */}
            <div className="keywords-page__table table-content--simplebar">
              <div className="accordion-table-frame">
                {/* Bez `maxHeight` — tabela rośnie ze stroną, SimpleBar przewija tylko w poziomie. */}
                <SimpleBar autoHide={false}>
                  <DataTable
                    stripedRows
                    rowClassName={(row: KeywordGroup) =>
                      row.brandStatus === 'INACTIVE' ? 'keywords-page__row--off' : ''
                    }
                    scrollable
                    removableSort
                    sortField={sortField ?? undefined}
                    sortOrder={sortOrder}
                    onSort={(e) => {
                      setSortField((e.sortField as 'keyword' | 'promptCount') || null);
                      setSortOrder((e.sortOrder as 1 | -1 | 0) ?? 0);
                      setFirst(0);
                    }}
                    value={pageItems}
                    dataKey="canonical"
                    tableStyle={{ minWidth: '30rem', tableLayout: 'auto' }}
                    tableClassName={`table${selectionMode ? ' table--select' : ''}`}
                    className="table-wrapper table-wrapper--accordion table-wrapper--stack"
                    responsiveLayout="stack"
                    emptyMessage={t('noKeywordsFound')}
                  >
                    <Column
                      key="checkbox"
                      exportable={false}
                      hidden={!selectionMode}
                      headerStyle={{ width: '25px' }}
                      bodyClassName="table__cell--stack-select"
                      header={
                        <Checkbox
                          checked={
                            filteredKeywords.filter(isRowSelectable).length > 0 &&
                            filteredKeywords
                              .filter(isRowSelectable)
                              .every((kw) => selectedKeywords.some((s) => s.canonical === kw.canonical))
                          }
                          onChange={(e) => {
                            setSelectedKeywords(e.checked ? filteredKeywords.filter(isRowSelectable) : []);
                          }}
                        />
                      }
                      body={(row: KeywordGroup) => (
                        <Checkbox
                          checked={selectedKeywords.some((s) => s.canonical === row.canonical)}
                          onChange={(e) => {
                            if (!isRowSelectable(row)) return;
                            if (e.checked) {
                              setSelectedKeywords((prev) => [...prev, row]);
                            } else {
                              setSelectedKeywords((prev) =>
                                prev.filter((s) => s.canonical !== row.canonical)
                              );
                            }
                          }}
                          disabled={!isRowSelectable(row)}
                        />
                      )}
                    />
                    <Column
                      sortable
                      field="keyword"
                      header={t('brand')}
                      body={brandBodyTemplate}
                      bodyClassName="table__cell--stack-title"
                      headerClassName="table__header"
                      style={{ minWidth: '200px', maxWidth: '100%' }}
                    />
                    <Column
                      header={t('trackedNames')}
                      body={trackedNamesBodyTemplate}
                      headerClassName="table__header"
                      style={{ minWidth: '180px' }}
                    />
                    <Column
                      header={t('brandDomains')}
                      body={domainsBodyTemplate}
                      headerClassName="table__header"
                      style={{ width: '220px' }}
                    />
                    <Column
                      sortable
                      field="promptCount"
                      header={t('mentions')}
                      body={promptCountBodyTemplate}
                      headerClassName="table__header table__header--center"
                      bodyClassName="table__cell--center"
                      style={{ width: '120px' }}
                    />
                    <Column
                      header={t('status')}
                      body={statusBodyTemplate}
                      frozen
                      alignFrozen="right"
                      headerClassName="table__header table__header--center"
                      bodyClassName="table__cell table__cell--center table__cell--stack-actions"
                      style={{ width: '80px' }}
                    />
                  </DataTable>
                </SimpleBar>
                <Paginator
                  className="pagination"
                  first={pageFirst}
                  rows={rows}
                  totalRecords={sortedKeywords.length}
                  rowsPerPageOptions={[10, 25, 50]}
                  currentPageReportTemplate={t('rowsNumber') + ':'}
                  template={
                    isMobile
                      ? MOBILE_PAGINATOR_TEMPLATE
                      : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
                  }
                  pageLinkSize={isMobile ? 3 : 5}
                  onPageChange={(e) => {
                    setFirst(e.first);
                    setRows(e.rows);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <BrandDialog
        projectId={projectIdNumber}
        visible={isDialogOpen && liveEditingGroup !== null}
        group={editingGroup}
        tab={editing?.tab ?? 'basic'}
        onTabChange={(tab) => setEditing((current) => (current ? { ...current, tab } : current))}
        onHide={() => setIsDialogOpen(false)}
        domain={editingGroup ? primaryDomainOf(editingGroup) : undefined}
        color={editing?.color}
        domains={editingGroup ? groupDomainsOf(editingGroup) : []}
        targets={editingGroup ? targetsFor(editingGroup) : []}
        isLocked={isLockedName}
        status={{
          disabled: !editingGroup || isGroupToggleDisabled(editingGroup) || groupToggleMutation.isPending,
          reason: editingGroup && isGroupToggleDisabled(editingGroup) ? getDisabledReason(editingGroup) : '',
          onToggle: () => editingGroup && handleGroupToggle(editingGroup),
        }}
        onError={handleMutationError}
      />

      {toastElement}
    </div>
  );
};

export default KeywordsPage;
